import { Middleware } from '@reduxjs/toolkit';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import {
  AnalyticsEvent,
  AnalyticsEventProperties,
  InsightsDataSource,
} from 'services/analytics-events';
import { describeFilters } from 'services/analytics-filters';
import {
  cancelTwoFactorSetup,
  changePassword,
  confirmTwoFactorSetup,
  createNotificationSubscription,
  createUpdateVersion,
  createVersionSet,
  deleteNotificationSubscriptionById,
  deleteUpdateVersionById,
  deleteVersionSetById,
  disableTwoFactor,
  initTwoFactorSetup,
  login,
  patchNotificationSubscriptionById,
  patchUpdateVersionById,
  patchVersionSetById,
  postResetPasswordEmail,
  postSubscribeToOrganizationById,
  postSubscribeToRobotById,
  registerNotificationDevice,
  registerUser,
  resetUserTwoFactor,
  setPassword,
  validateUserInvite,
  verifyTwoFactor,
} from 'store/general/general-actions';
import {
  createApiKey,
  createSubOrganization,
  createUser,
  deleteApiKeyById,
  deleteOrganizationById,
  deleteUserById,
  patchOrganizationById,
  patchUserById,
} from 'store/organization/organization-actions';
import {
  createNewFleetDashboard,
  createNewFleetDataSet,
  deleteDashboardById,
  deleteDataSetById,
  getFleetDataByFilters,
  getInsights,
  saveFleetDashboard,
  saveFleetDataSet,
} from 'store/fleet-management/fleet-management-actions';
import {
  deleteCleaningZoneById,
  deleteGateById,
  deleteLandmarkById,
  deleteRobotMissionById,
  deleteRobotScheduleById,
  patchDisableAllSchedules,
  patchEnableAllSchedules,
  patchRobotMission,
  patchRobotSchedule,
  postNewRobotMission,
  postNewRobotSchedule,
  deleteRedZoneById,
  deleteRuleZoneById,
  patchCleaningZone,
  patchFloor,
  patchGateById,
  patchLandmark,
  patchLimitZone,
  patchRedZone,
  patchRoomById,
  patchRuleZone,
  postNewCleaningZone,
  postNewGate,
  postNewLandmark,
  postNewRedZone,
  postNewRuleZone,
  upsertWaypointSet,
} from 'store/local-robot/local-robot-actions';
import {
  deleteExcludedDateById,
  patchCustomEnvironmentPollution,
  patchExcludedDateById,
  patchNavLocalPlanner,
  patchNavTasks,
  patchParamList,
  postExcludedDate,
} from 'store/local-robot-configuration/local-robot-configuration-actions';
import { patchRobotAuthenticationSettingsById, patchRobotById } from 'store/robots/robots-actions';
import LoginVariant from 'models/enums/login-variant';
import { RobotSettingArea } from 'services/analytics-events';

/*
 * Turns the outcome of feature thunks into analytics events, so the components that start them
 * need no tracking code. A fulfilled thunk sends its event; a rejected one sends
 * `Operation Failed` naming the operation (the HTTP detail is already in `API Request Failed`).
 *
 * Properties are built only from enum values, counts, booleans and field *names* — never from the
 * values a user typed. See the privacy rules in services/analytics-events.ts.
 */

/** The part of an async thunk the middleware needs: its fulfilled/rejected matchers. */
interface AnyThunk {
  fulfilled: { match: (action: unknown) => boolean };
  rejected: { match: (action: unknown) => boolean };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ThunkAction = { payload: any; meta: { arg: any } };

interface Rule {
  thunk: AnyThunk;
  /** Stable name for the operation, used in `Operation Failed`. */
  operation: string;
  onSuccess: (action: ThunkAction, state: unknown) => void;
}

const rule = <E extends AnalyticsEvent>(
  thunk: AnyThunk,
  operation: string,
  event: E,
  properties: (action: ThunkAction, state: unknown) => AnalyticsEventProperties[E],
): Rule => ({
  thunk,
  operation,
  onSuccess: (action, state) => trackEvent(event, properties(action, state)),
});

const none = () => ({}) as Record<string, never>;

const fieldNames = (data: unknown): string[] =>
  data != null && typeof data === 'object' ? Object.keys(data).sort() : [];

/** What every `Insights Data Loaded` reports about the answer itself. */
const dataLoad = (payload: { loadTime: number; data: unknown; payloadBytes: number | null }) => ({
  latency_ms: Math.round(payload.loadTime),
  payload_bytes: payload.payloadBytes,
  row_count: Array.isArray(payload.data) ? payload.data.length : null,
  source_page: getCurrentPageName(),
});

const loginVariant = (arg: { variant?: string } | undefined) => arg?.variant ?? LoginVariant.Local;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const currentRobotId = (state: any): string | null =>
  state?.robots?.robots?.currentRobot?.id ?? null;

const zoneRule = (
  thunk: AnyThunk,
  zoneType: string,
  operation: 'created' | 'updated' | 'deleted',
): Rule =>
  rule(thunk, `zone_${zoneType}_${operation}`, AnalyticsEvent.ZonePlanSaved, (_, state) => ({
    zone_type: zoneType,
    operation,
    robot_id: currentRobotId(state),
  }));

type Operation = 'created' | 'updated' | 'deleted';

// Robot tabs are a `tab` query parameter; they tell e.g. the missions tab from the button
// configuration in robot settings, which both edit missions.
const currentRobotTab = (): string | null => {
  const tab = new URLSearchParams(window.location.search).get('tab');
  return tab != null && /^[a-z0-9_-]{1,40}$/i.test(tab) ? tab : null;
};

// Updates send the changed field names; creates the fields that were set.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const changedFields = (arg: any, operation: Operation): string[] =>
  operation === 'updated' ? fieldNames(arg?.data) : operation === 'created' ? fieldNames(arg) : [];

const missionRule = (thunk: AnyThunk, operation: Operation): Rule => {
  const event =
    operation === 'created'
      ? AnalyticsEvent.MissionCreated
      : operation === 'updated'
        ? AnalyticsEvent.MissionUpdated
        : AnalyticsEvent.MissionDeleted;
  return rule(thunk, `mission_${operation}`, event, ({ meta }, state) => ({
    robot_id: currentRobotId(state),
    source_tab: currentRobotTab(),
    changed_fields: changedFields(meta.arg, operation),
  }));
};

const scheduleRule = (thunk: AnyThunk, operation: Operation): Rule => {
  const event =
    operation === 'created'
      ? AnalyticsEvent.ScheduleCreated
      : operation === 'updated'
        ? AnalyticsEvent.ScheduleUpdated
        : AnalyticsEvent.ScheduleDeleted;
  return rule(thunk, `schedule_${operation}`, event, ({ meta }, state) => ({
    robot_id: currentRobotId(state),
    changed_fields: changedFields(meta.arg, operation),
  }));
};

const settingRule = (thunk: AnyThunk, area: RobotSettingArea, operation: Operation): Rule =>
  rule(
    thunk,
    `robot_setting_${area}_${operation}`,
    AnalyticsEvent.RobotSettingChanged,
    ({ meta }, state) => ({
      setting_area: area,
      operation,
      robot_id: currentRobotId(state),
      // Parameter patches are a flat partial object, not { id, data }.
      changed_fields:
        operation === 'updated' && meta.arg?.data == null
          ? fieldNames(meta.arg)
          : changedFields(meta.arg, operation),
    }),
  );

const mapConfigurationRule = (thunk: AnyThunk, target: 'floor' | 'room'): Rule => ({
  thunk,
  operation: `map_configuration_${target}_updated`,
  onSuccess: ({ meta }, state) => {
    const fields = fieldNames(meta.arg?.data);
    // A room's button configuration is edited from its own screen; report it as a setting.
    if (target === 'room' && fields.includes('buttonConfiguration')) {
      trackEvent(AnalyticsEvent.RobotSettingChanged, {
        setting_area: RobotSettingArea.RoomButtonConfiguration,
        operation: 'updated',
        robot_id: currentRobotId(state),
        changed_fields: fields,
      });
      return;
    }
    trackEvent(AnalyticsEvent.MapConfigurationChanged, {
      target,
      robot_id: currentRobotId(state),
      changed_fields: fields,
    });
  },
});

const RULES: Rule[] = [
  // Sign-in, registration and account security
  {
    thunk: login,
    operation: 'login',
    onSuccess: ({ payload, meta }) => {
      if (payload != null && 'requires2fa' in payload) {
        trackEvent(AnalyticsEvent.TwoFactorChallengeShown, {
          login_variant: loginVariant(meta.arg),
          setup_required: payload.setupRequired === true,
        });
      } else {
        trackEvent(AnalyticsEvent.LoginSucceeded, { login_variant: loginVariant(meta.arg) });
      }
    },
  },
  rule(verifyTwoFactor, 'two_factor_verify', AnalyticsEvent.TwoFactorVerified, ({ meta }) => ({
    method: meta.arg?.backupCode ? 'backup_code' : 'otp',
  })),
  rule(
    initTwoFactorSetup,
    'two_factor_setup_start',
    AnalyticsEvent.TwoFactorSetupStarted,
    ({ meta }) => ({ is_required_at_login: meta.arg != null }),
  ),
  rule(
    confirmTwoFactorSetup,
    'two_factor_setup_confirm',
    AnalyticsEvent.TwoFactorSetupCompleted,
    ({ meta }) => ({ is_required_at_login: meta.arg?.pendingToken != null }),
  ),
  rule(
    cancelTwoFactorSetup,
    'two_factor_setup_cancel',
    AnalyticsEvent.TwoFactorSetupCancelled,
    none,
  ),
  rule(disableTwoFactor, 'two_factor_disable', AnalyticsEvent.TwoFactorDisabled, ({ meta }) => ({
    method: meta.arg?.backupCode
      ? 'backup_code'
      : meta.arg?.otp
        ? 'otp'
        : meta.arg?.googleToken
          ? 'google'
          : 'password',
  })),
  rule(resetUserTwoFactor, 'user_two_factor_reset', AnalyticsEvent.UserTwoFactorReset, none),
  rule(changePassword, 'password_change', AnalyticsEvent.PasswordChanged, none),
  rule(
    postResetPasswordEmail,
    'password_reset_request',
    AnalyticsEvent.PasswordResetRequested,
    none,
  ),
  rule(setPassword, 'password_set', AnalyticsEvent.PasswordSet, ({ payload }) => ({
    requires_two_factor: payload != null && 'requires2fa' in payload,
  })),
  rule(validateUserInvite, 'invite_validate', AnalyticsEvent.InviteValidated, none),
  rule(registerUser, 'registration', AnalyticsEvent.RegistrationCompleted, none),

  // Organisation and user management
  rule(
    createSubOrganization,
    'organization_create',
    AnalyticsEvent.OrganizationCreated,
    ({ meta }) => ({ organization_role: meta.arg?.role ?? null }),
  ),
  rule(
    patchOrganizationById,
    'organization_update',
    AnalyticsEvent.OrganizationUpdated,
    ({ meta }) => ({ changed_fields: fieldNames(meta.arg?.data) }),
  ),
  rule(deleteOrganizationById, 'organization_delete', AnalyticsEvent.OrganizationDeleted, none),
  rule(createUser, 'user_invite', AnalyticsEvent.UserInvited, ({ meta }) => ({
    invited_role: meta.arg?.role ?? null,
  })),
  rule(patchUserById, 'user_update', AnalyticsEvent.UserUpdated, ({ meta }) => ({
    patch_type: meta.arg?.patchType ?? 'unknown',
    changed_fields: fieldNames(meta.arg?.data),
  })),
  rule(deleteUserById, 'user_delete', AnalyticsEvent.UserDeleted, ({ meta }) => ({
    is_self: meta.arg?.deletingSelf === true,
  })),
  rule(createApiKey, 'api_key_create', AnalyticsEvent.ApiKeyCreated, ({ meta }) => ({
    scope_count: Array.isArray(meta.arg?.scopes) ? meta.arg.scopes.length : 0,
  })),
  rule(deleteApiKeyById, 'api_key_delete', AnalyticsEvent.ApiKeyDeleted, none),

  // Software versions
  rule(createVersionSet, 'version_set_create', AnalyticsEvent.VersionSetCreated, none),
  rule(patchVersionSetById, 'version_set_update', AnalyticsEvent.VersionSetUpdated, ({ meta }) => ({
    changed_fields: fieldNames(meta.arg?.data),
  })),
  rule(deleteVersionSetById, 'version_set_delete', AnalyticsEvent.VersionSetDeleted, none),
  rule(createUpdateVersion, 'software_version_create', AnalyticsEvent.SoftwareVersionCreated, none),
  rule(
    patchUpdateVersionById,
    'software_version_update',
    AnalyticsEvent.SoftwareVersionUpdated,
    ({ meta }) => ({ changed_fields: fieldNames(meta.arg?.data) }),
  ),
  rule(
    deleteUpdateVersionById,
    'software_version_delete',
    AnalyticsEvent.SoftwareVersionDeleted,
    none,
  ),

  // Notifications
  rule(
    createNotificationSubscription,
    'notification_subscription_create',
    AnalyticsEvent.NotificationSubscriptionCreated,
    ({ meta }) => ({
      trigger: meta.arg?.trigger ?? null,
      repeat: meta.arg?.repeat ?? null,
      email_active: meta.arg?.emailActive ?? null,
      push_active: meta.arg?.pushActive ?? null,
    }),
  ),
  rule(
    patchNotificationSubscriptionById,
    'notification_subscription_update',
    AnalyticsEvent.NotificationSubscriptionUpdated,
    ({ meta }) => ({
      changed_fields: fieldNames(meta.arg?.data),
      email_active: meta.arg?.data?.emailActive ?? null,
      push_active: meta.arg?.data?.pushActive ?? null,
    }),
  ),
  rule(
    deleteNotificationSubscriptionById,
    'notification_subscription_delete',
    AnalyticsEvent.NotificationSubscriptionDeleted,
    none,
  ),
  rule(
    postSubscribeToRobotById,
    'robot_subscription_toggle',
    AnalyticsEvent.RobotSubscriptionToggled,
    ({ payload, meta }) => ({
      is_subscribed: Array.isArray(payload) && payload.some((s) => s?.id === meta.arg),
    }),
  ),
  rule(
    postSubscribeToOrganizationById,
    'organization_subscription_toggle',
    AnalyticsEvent.OrganizationSubscriptionToggled,
    ({ payload, meta }) => ({
      is_subscribed: Array.isArray(payload) && payload.some((s) => s?.id === meta.arg),
    }),
  ),
  rule(
    registerNotificationDevice,
    'push_notifications_enable',
    AnalyticsEvent.PushNotificationsEnabled,
    none,
  ),

  // Insights dashboards and datasets
  rule(
    createNewFleetDashboard,
    'insights_dashboard_create',
    AnalyticsEvent.InsightsDashboardCreated,
    ({ payload }) => ({
      view_type: payload?.viewType ?? null,
      widget_count: Array.isArray(payload?.widgets) ? payload.widgets.length : 0,
    }),
  ),
  rule(
    saveFleetDashboard,
    'insights_dashboard_save',
    AnalyticsEvent.InsightsDashboardSaved,
    ({ payload }) => ({
      view_type: payload?.viewType ?? null,
      widget_count: Array.isArray(payload?.widgets) ? payload.widgets.length : 0,
    }),
  ),
  rule(
    deleteDashboardById,
    'insights_dashboard_delete',
    AnalyticsEvent.InsightsDashboardDeleted,
    none,
  ),
  rule(createNewFleetDataSet, 'dataset_create', AnalyticsEvent.DatasetCreated, none),
  rule(saveFleetDataSet, 'dataset_save', AnalyticsEvent.DatasetSaved, none),
  rule(deleteDataSetById, 'dataset_delete', AnalyticsEvent.DatasetDeleted, none),
  rule(
    getInsights,
    'insights_data_load',
    AnalyticsEvent.InsightsDataLoaded,
    ({ payload, meta }) => ({
      data_source: InsightsDataSource.Insights,
      // `<robot id>/robot-performance` names the endpoint by its last segment.
      endpoint: meta.arg.path.split('/').pop(),
      ...dataLoad(payload),
      ...describeFilters(meta.arg.query, 'start', 'end'),
    }),
  ),
  rule(
    getFleetDataByFilters,
    'dataset_data_load',
    AnalyticsEvent.InsightsDataLoaded,
    ({ payload, meta }) => {
      const dataset = meta.arg.payload;
      const fields = new Set<string>(dataset.filters.map((f: { field: string }) => f.field));
      return {
        data_source: InsightsDataSource.Dataset,
        endpoint: dataset.model,
        ...dataLoad(payload),
        ...describeFilters({ start: dataset.start, end: dataset.end }, 'start', 'end'),
        filter_keys: [...fields].sort(),
      };
    },
  ),

  // Zone planner and map configuration
  zoneRule(postNewCleaningZone, 'cleaning_zone', 'created'),
  zoneRule(patchCleaningZone, 'cleaning_zone', 'updated'),
  zoneRule(deleteCleaningZoneById, 'cleaning_zone', 'deleted'),
  zoneRule(postNewRedZone, 'red_zone', 'created'),
  zoneRule(patchRedZone, 'red_zone', 'updated'),
  zoneRule(deleteRedZoneById, 'red_zone', 'deleted'),
  zoneRule(patchLimitZone, 'limit_zone', 'updated'),
  zoneRule(postNewRuleZone, 'rule_zone', 'created'),
  zoneRule(patchRuleZone, 'rule_zone', 'updated'),
  zoneRule(deleteRuleZoneById, 'rule_zone', 'deleted'),
  zoneRule(postNewLandmark, 'landmark', 'created'),
  zoneRule(patchLandmark, 'landmark', 'updated'),
  zoneRule(deleteLandmarkById, 'landmark', 'deleted'),
  zoneRule(upsertWaypointSet, 'waypoint_set', 'updated'),
  zoneRule(postNewGate, 'gate', 'created'),
  zoneRule(patchGateById, 'gate', 'updated'),
  zoneRule(deleteGateById, 'gate', 'deleted'),

  // Map and room configuration
  mapConfigurationRule(patchFloor, 'floor'),
  mapConfigurationRule(patchRoomById, 'room'),

  // Missions and schedules
  missionRule(postNewRobotMission, 'created'),
  missionRule(patchRobotMission, 'updated'),
  missionRule(deleteRobotMissionById, 'deleted'),
  scheduleRule(postNewRobotSchedule, 'created'),
  scheduleRule(patchRobotSchedule, 'updated'),
  scheduleRule(deleteRobotScheduleById, 'deleted'),
  rule(
    patchEnableAllSchedules,
    'schedules_enable_all',
    AnalyticsEvent.SchedulesToggled,
    (_, state) => ({ enabled: true, robot_id: currentRobotId(state) }),
  ),
  rule(
    patchDisableAllSchedules,
    'schedules_disable_all',
    AnalyticsEvent.SchedulesToggled,
    (_, state) => ({ enabled: false, robot_id: currentRobotId(state) }),
  ),

  // Robot settings
  settingRule(patchParamList, RobotSettingArea.CleaningParameters, 'updated'),
  settingRule(patchCustomEnvironmentPollution, RobotSettingArea.EnvironmentPollution, 'updated'),
  settingRule(patchNavLocalPlanner, RobotSettingArea.NavigationLocalPlanner, 'updated'),
  settingRule(patchNavTasks, RobotSettingArea.NavigationTasks, 'updated'),
  settingRule(postExcludedDate, RobotSettingArea.ExcludedDates, 'created'),
  settingRule(patchExcludedDateById, RobotSettingArea.ExcludedDates, 'updated'),
  settingRule(deleteExcludedDateById, RobotSettingArea.ExcludedDates, 'deleted'),
  settingRule(patchRobotById, RobotSettingArea.RobotDetails, 'updated'),
  settingRule(patchRobotAuthenticationSettingsById, RobotSettingArea.AccessSettings, 'updated'),
];

const statusCode = (payload: unknown): number | null => {
  const code = (payload as { code?: unknown } | null)?.code;
  const parsed = typeof code === 'string' ? Number(code) : typeof code === 'number' ? code : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

export const analyticsMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  for (const r of RULES) {
    if (r.thunk.fulfilled.match(action)) {
      try {
        r.onSuccess(action as unknown as ThunkAction, store.getState());
      } catch {
        // Analytics must never break the app.
      }
      break;
    }
    if (r.thunk.rejected.match(action)) {
      // A thunk aborted by the app (e.g. a superseded request) is not a failure.
      if ((action as { meta?: { aborted?: boolean } }).meta?.aborted) break;
      trackEvent(AnalyticsEvent.OperationFailed, {
        operation: r.operation,
        status_code: statusCode((action as { payload?: unknown }).payload),
      });
      break;
    }
  }

  return result;
};
